import { Code2, Layers, Siren, Wrench, Car, Shirt, Package, FileText, Paintbrush2 } from "lucide-react";
import { Callout, CodeBlock, Tag, FileFormatRow } from "@/components/GuidesShared";
import { StepList, BulletList } from "@/lib/guides/primitives";
import type { GuideSectionDef } from "@/lib/guides/types";

export const gta5Sections: GuideSectionDef[] = [
  {
    id: "file-formats",
    label: "GTA5 File Formats",
    icon: <FileText className="h-5 w-5" />,
    shortDescription: "What .yft, .ydd, .ytd, .ymap, and friends actually are.",
    seoTitle: "GTA5 File Formats Explained — YFT, YDD, YTD & More (FiveM)",
    seoDescription:
      "What .yft, .ydr, .ydd, .ytd, .ymap, .ybn, .ycd, and .awc files are in GTA5 and FiveM, and which tools open each one — a plain-English reference for modders.",
    keywords: [
      "GTA5 file formats",
      "what is a yft file",
      "ydd file",
      "ytd texture dictionary",
      "FiveM file extensions",
      "OpenIV file types",
    ],
    toolHref: "/creator-convert",
    toolLabel: "Creator Convert",
    faqs: [
      {
        q: "What is a .yft file in GTA5?",
        a: "A YFT (Fragment) is a vehicle model with its physics fragments — the primary file for any FiveM vehicle. Static objects like props and weapons use .ydr (Drawable) instead.",
      },
      {
        q: "What's the difference between .ydd and .ytd?",
        a: "A YDD (Drawable Dictionary) holds 3D models such as EUP clothing, while a YTD (Texture Dictionary) is a container of the DDS textures those models reference. Clothing needs both — the YDD for the mesh and a matching YTD for its textures.",
      },
      {
        q: "How do I open GTA5 y-prefixed files?",
        a: "Files that start with 'y' (yft, ydd, ytd, ymap…) are Rockstar RPF resources. Open and export them with OpenIV or CodeWalker, then inspect models in EUP Viewer or Paint Booth and textures after exporting to DDS or PNG.",
      },
      {
        q: "What is an .awc file used for?",
        a: "An AWC (Audio Wave Container) stores audio samples. In FiveM vehicle work you'll meet it when adding custom siren tones or engine sounds.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          GTA5 uses Rockstar's proprietary resource format system. The extensions below are the
          ones you'll encounter most often when working on FiveM vehicle and clothing mods.
        </p>
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/40">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-foreground w-24">Extension</th>
                <th className="px-4 py-3 text-left font-medium text-foreground">Full name</th>
                <th className="px-4 py-3 text-left font-medium text-foreground">Description</th>
              </tr>
            </thead>
            <tbody>
              <FileFormatRow ext=".yft" name="Fragment (YFT)" desc="Vehicle model with physics fragments. The primary file for vehicles." />
              <FileFormatRow ext=".ydr" name="Drawable (YDR)" desc="Static 3D model — used for props, weapons, and map objects." />
              <FileFormatRow ext=".ydd" name="Drawable Dictionary (YDD)" desc="Collection of drawables. Used for character clothing (EUP)." />
              <FileFormatRow ext=".ytd" name="Texture Dictionary (YTD)" desc="Container for textures (DDS images). Referenced by models." />
              <FileFormatRow ext=".ymap" name="Map (YMAP)" desc="Defines object placements in the game world." />
              <FileFormatRow ext=".ybn" name="Bounds (YBN)" desc="Collision mesh for physics interactions." />
              <FileFormatRow ext=".ycd" name="Clip Dictionary (YCD)" desc="Animation clips and sequences." />
              <FileFormatRow ext=".awc" name="Audio Wave Container (AWC)" desc="Audio samples, used for custom sirens and sounds." />
              <FileFormatRow ext=".dat" name="Data (DAT)" desc="Various game data tables (e.g., popgroups, vehiclelayouts)." />
            </tbody>
          </table>
        </div>
        <Callout type="info">
          Formats prefixed with <strong>y</strong> are Rockstar's RPF resource format. They can
          be opened with <strong>OpenIV</strong> or <strong>CodeWalker</strong>.
        </Callout>
      </>
    ),
  },
  {
    id: "vehicle-meta-files",
    label: "Vehicle Meta Files",
    icon: <Car className="h-5 w-5" />,
    shortDescription: "vehicles.meta, handling.meta, carcols, carvariations explained.",
    seoTitle: "FiveM Vehicle Meta Files — vehicles.meta & handling.meta Guide",
    seoDescription:
      "A full reference for the four FiveM vehicle meta files — vehicles.meta, handling.meta, carvariations.meta, and carcols.meta — with key fields, vehicle class IDs, and fixes for common spawn issues.",
    keywords: [
      "vehicles.meta",
      "handling.meta",
      "FiveM vehicle meta files",
      "vehicle class ids",
      "carvariations.meta",
      "vehicle won't spawn FiveM",
    ],
    toolHref: "/easy-meta",
    toolLabel: "Easy Meta",
    faqs: [
      {
        q: "What meta files does a FiveM vehicle need?",
        a: "At minimum four: vehicles.meta (identity, class, audio), handling.meta (physics), carvariations.meta (liveries and extras), and carcols.meta (sirens and mod kits). Easy Meta generates all four from a guided form.",
      },
      {
        q: "Why won't my FiveM vehicle spawn?",
        a: "The usual cause is a modelName in vehicles.meta that doesn't exactly match the .yft filename (always lowercase), or a wrong stream path in fxmanifest.lua. A black or untextured model usually means txdName doesn't match the .ytd.",
      },
      {
        q: "What is handlingId and why must it match?",
        a: "handlingId in vehicles.meta links the vehicle to its physics entry in handling.meta — it must equal that entry's handlingName exactly, or the car falls back to default handling.",
      },
      {
        q: "What vehicle class should I use?",
        a: "vehicleClass is an integer 0–20 (for example 18 = Emergency, 7 = Super, 12 = Van). It sets the in-game category and spawn behaviour, so pick the class that matches your vehicle type.",
      },
      {
        q: "How do I set the right engine sound?",
        a: "audioNameHash controls engine and exhaust audio. Use a guaranteed vanilla name like POLICE or SULTAN to inherit its sounds; a name that doesn't exist in context produces the wrong sound or none at all.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Every custom vehicle in FiveM requires a set of XML meta files that tell the game
          engine how to treat the vehicle. These four files are the minimum required set.
        </p>
        <div className="space-y-4">
          {[
            {
              tag: "vehicles.meta",
              badge: "Required",
              body: "The core identity file — registers the model with the game engine, sets its class, audio, layout, and spawn behaviour. Full field reference is below.",
            },
            {
              tag: "handling.meta",
              badge: "Required",
              body: (
                <>
                  Controls all physics parameters. The most important values are{" "}
                  <code className="rounded bg-muted px-1 text-xs font-mono">fMass</code>,{" "}
                  <code className="rounded bg-muted px-1 text-xs font-mono">fInitialDriveForce</code>,{" "}
                  <code className="rounded bg-muted px-1 text-xs font-mono">fDriveInertia</code>, and the
                  traction/suspension blocks. Always start from a known-good vanilla vehicle and
                  adjust incrementally.
                </>
              ),
            },
            {
              tag: "carvariations.meta",
              badge: "Required",
              body: "Maps livery indices to texture dictionary entries and configures paint layer behaviour. Each variation entry defines which YTD textures are loaded for that livery slot and what extras are spawned with it.",
            },
            {
              tag: "carcols.meta",
              badge: "Required for sirens",
              body: (
                <>
                  Defines emergency lighting kits and siren colour sequences. Each{" "}
                  <code className="rounded bg-muted px-1 text-xs font-mono">Kit</code> entry links
                  to a set of light IDs and their flash patterns, which correspond to the sequences
                  you build in Blinklab.
                </>
              ),
            },
          ].map(({ tag, badge, body }) => (
            <div key={tag} className="rounded-xl border border-border/60 bg-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <Tag>{tag}</Tag>
                <span className="text-xs text-muted-foreground">{badge}</span>
              </div>
              <p className="text-sm text-muted-foreground leading-6">{body}</p>
            </div>
          ))}
        </div>

        <p className="text-sm font-medium text-foreground mt-8">vehicles.meta — field reference</p>
        <p className="text-sm leading-7 text-muted-foreground">
          <Tag>vehicles.meta</Tag> is the core file that defines a vehicle's properties in FiveM.
          It uses binary XML format — edit it as plain text in any text editor after exporting
          from OpenIV.
        </p>

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mt-6 mb-2">Core identity</p>
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/40">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-foreground w-44">Field</th>
                <th className="px-4 py-2.5 text-left font-medium text-foreground">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40 text-muted-foreground">
              {[
                ["modelName", "Internal spawn name (e.g. police3). Must match your .yft filename exactly. Always lowercase."],
                ["txdName", "Texture dictionary name the model references. Usually identical to modelName."],
                ["handlingId", "Links to the handling entry in handling.meta. Must match the handlingName value there."],
                ["gameName", "Display name shown in-game (e.g. in the chop shop). Usually a GXT label."],
                ["vehicleMakeName", "Manufacturer label shown in the HUD (e.g. BRAVADO). Can be a custom GXT key or a vanilla value."],
                ["audioNameHash", "Determines engine and exhaust sounds. Use a vanilla vehicle name (e.g. POLICE) to inherit its audio."],
              ].map(([field, desc]) => (
                <tr key={field}>
                  <td className="px-4 py-2.5 font-mono text-xs text-foreground">{field}</td>
                  <td className="px-4 py-2.5 text-xs">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mt-6 mb-2">Layout & camera</p>
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/40">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-foreground w-44">Field</th>
                <th className="px-4 py-2.5 text-left font-medium text-foreground">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40 text-muted-foreground">
              {[
                ["layout", "Vehicle layout hash — controls door/seat animations and enter/exit positions. Match to a vanilla vehicle with a similar door count and silhouette."],
                ["cameraName", "Third-person camera rig. Controls how far back and high the camera sits."],
                ["aimCameraName", "Camera used when aiming a weapon from the vehicle."],
                ["bonnetCameraName", "Bonnet (hood) cam preset name."],
                ["povCameraName", "First-person camera preset."],
                ["PovCameraOffset", "XYZ offset applied to the first-person camera inside the cabin."],
              ].map(([field, desc]) => (
                <tr key={field}>
                  <td className="px-4 py-2.5 font-mono text-xs text-foreground">{field}</td>
                  <td className="px-4 py-2.5 text-xs">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mt-6 mb-2">Visual & physics</p>
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/40">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-foreground w-44">Field</th>
                <th className="px-4 py-2.5 text-left font-medium text-foreground">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40 text-muted-foreground">
              {[
                ["wheelScale", "Front wheel render scale multiplier. 1.0 = model default."],
                ["wheelScaleRear", "Rear wheel render scale multiplier."],
                ["defaultBodyHealth", "Starting body health. Vanilla vehicles use 1000.0."],
                ["damageMapScale", "Scales how quickly visual damage accumulates (dents, scratches)."],
                ["dirtLevelMax", "Maximum dirt accumulation level (0.0 – 1.0)."],
                ["HDTextureDist", "Distance at which the high-detail texture is swapped in."],
                ["lodDistances", "Array of four LOD switch distances (near → far)."],
              ].map(([field, desc]) => (
                <tr key={field}>
                  <td className="px-4 py-2.5 font-mono text-xs text-foreground">{field}</td>
                  <td className="px-4 py-2.5 text-xs">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mt-6 mb-2">Spawning & performance</p>
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/40">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-foreground w-44">Field</th>
                <th className="px-4 py-2.5 text-left font-medium text-foreground">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40 text-muted-foreground">
              {[
                ["vehicleClass", "Integer 0–20 that determines the vehicle category (see table below)."],
                ["vehicleType", "Engine type: AUTOMOBILE, BIKE, QUAD, PLANE, HELI, BOAT, SUBMARINE, TRAILER."],
                ["plateType", "Licence plate style (0 = blue/white, 1 = yellow/black, etc.)."],
                ["wheelType", "Wheel category for mod shop options (0 = Sport, 1 = Muscle, etc.)."],
                ["estimatedMaxSpeed", "Approximate top speed in m/s (0–300). Used by AI pathfinding and the minimap."],
                ["maxNum", "Max simultaneous instances spawned in traffic."],
                ["frequency", "Traffic spawn frequency weight relative to other vehicles in the same class."],
                ["swankness", "Prestige level that determines spawn areas (0 = common, 4 = very exclusive)."],
                ["seats", "Number of passenger seats (driver is not counted)."],
                ["visibleSpawnDistScale", "Multiplier on the distance at which the vehicle becomes visible."],
              ].map(([field, desc]) => (
                <tr key={field}>
                  <td className="px-4 py-2.5 font-mono text-xs text-foreground">{field}</td>
                  <td className="px-4 py-2.5 text-xs">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mt-6 mb-2">Vehicle classes (0–20)</p>
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/40">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-foreground w-12">ID</th>
                <th className="px-4 py-2.5 text-left font-medium text-foreground w-36">Class</th>
                <th className="px-4 py-2.5 text-left font-medium text-foreground">Example</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40 text-muted-foreground text-xs">
              {[
                [0,"Compact","Blista"],[1,"Sedan","Stanier"],[2,"SUV","Granger"],
                [3,"Coupe","Exemplar"],[4,"Muscle","Dominator"],[5,"Sports Classic","Monroe"],
                [6,"Sports","Elegy RH8"],[7,"Super","Adder"],[8,"Motorcycle","Bati 801"],
                [9,"Off-road","Mesa"],[10,"Industrial","Phantom"],[11,"Utility","Sadler"],
                [12,"Van","Rumpo"],[13,"Cycle","Tribike"],[14,"Boat","Dinghy"],
                [15,"Helicopter","Maverick"],[16,"Plane","Luxor"],[17,"Service","Taxi"],
                [18,"Emergency","Police"],[19,"Military","Rhino"],[20,"Commercial","Packer"],
              ].map(([id, cls, ex]) => (
                <tr key={id}>
                  <td className="px-4 py-2"><Tag>{id}</Tag></td>
                  <td className="px-4 py-2 font-medium text-foreground">{cls}</td>
                  <td className="px-4 py-2">{ex}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mt-6 mb-2">Minimal example</p>
        <CodeBlock>{`<Item type="CVehicleModelInfo__InitDataList__InitData">
  <modelName>my_police</modelName>
  <txdName>my_police</txdName>
  <handlingId>POLICE</handlingId>
  <gameName>MY_POLICE</gameName>
  <vehicleMakeName>CHEVAL</vehicleMakeName>
  <audioNameHash>POLICE</audioNameHash>
  <layout>LAYOUT_STD_HIGHSEAT</layout>
  <cameraName>DEFAULT_FOLLOW_VEHICLE_CAMERA</cameraName>
  <aimCameraName>DEFAULT_SCRIPTED_CAMERA</aimCameraName>
  <vehicleClass>18</vehicleClass>
  <vehicleType>AUTOMOBILE</vehicleType>
  <plateType>0</plateType>
  <wheelType>0</wheelType>
  <defaultBodyHealth value="1000.00" />
  <maxNum value="10" />
  <frequency value="30" />
  <seats value="3" />
</Item>`}</CodeBlock>
        <Callout type="tip">
          Always use <strong>lowercase</strong> for <code className="font-mono text-xs">modelName</code> and{" "}
          <code className="font-mono text-xs">txdName</code>. The game engine is case-sensitive on
          Linux-based servers and a mismatch will cause the vehicle to silently fail to load.
        </Callout>

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mt-6 mb-2">Common issues</p>
        <div className="space-y-3">
          {[
            { prob: "Vehicle doesn't spawn", fix: "modelName doesn't match the .yft filename, or the stream folder path in fxmanifest.lua is wrong." },
            { prob: "Textures missing / black model", fix: "txdName doesn't match the .ytd filename, or the .ytd isn't declared in the files block of fxmanifest.lua." },
            { prob: "Wrong engine sound", fix: "audioNameHash references a vehicle that doesn't exist in this context. Use a guaranteed vanilla value like POLICE or SULTAN." },
            { prob: "Broken enter/exit animations", fix: "layout value doesn't suit the vehicle body shape. Copy the layout from a vanilla vehicle with a similar door count and silhouette." },
            { prob: "AI drives erratically", fix: "estimatedMaxSpeed is set too high or too low. Match it to the actual top speed from fInitialDriveMaxFlatVel in handling.meta." },
          ].map(({ prob, fix }) => (
            <div key={prob} className="rounded-xl border border-border/60 bg-card p-4">
              <p className="text-sm font-medium text-foreground mb-1">{prob}</p>
              <p className="text-xs text-muted-foreground leading-5">{fix}</p>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: "fivem-resource-structure",
    label: "FiveM Resource Structure",
    icon: <Package className="h-5 w-5" />,
    shortDescription: "fxmanifest.lua, the stream/ folder, and what FiveM needs to load.",
    seoTitle: "FiveM Resource Structure — fxmanifest.lua & stream Folder Guide",
    seoDescription:
      "How a FiveM vehicle resource is laid out — the stream/ folder, the required data_file entries, and a working fxmanifest.lua example. Build it automatically with PackBench.",
    keywords: [
      "fxmanifest.lua",
      "FiveM resource structure",
      "stream folder",
      "FiveM vehicle addon",
      "data_file fxmanifest",
      "fx_version cerulean",
    ],
    toolHref: "/pack-bench",
    toolLabel: "PackBench",
    faqs: [
      {
        q: "What is fxmanifest.lua?",
        a: "It's the manifest at the root of every FiveM resource that tells the server what to load — the fx_version, the game, the streamed files, and the data_file entries for your meta files. The older __resource.lua name still works, but fxmanifest.lua is current.",
      },
      {
        q: "Why are my meta files being ignored?",
        a: "If you don't declare each one with a data_file line (VEHICLE_METADATA_FILE, HANDLING_FILE, VEHICLE_VARIATION_FILE, CARCOLS_FILE), FiveM never loads them — the vehicle spawns but uses default handling and no liveries. PackBench writes these lines for you.",
      },
      {
        q: "What goes in the stream/ folder?",
        a: "The streamed game assets: your .yft (and optional _hi.yft high-LOD), .ytd, and the vehicle .meta files. FiveM streams anything matched by the files{} block in fxmanifest.lua.",
      },
      {
        q: "Can I ship a resource as a ZIP?",
        a: "Yes — FiveM loads a resource from a folder or a ZIP as long as fxmanifest.lua is at the root. PackBench outputs exactly this, streaming-ready.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          FiveM loads resources from a folder (or ZIP) that must contain an{" "}
          <Tag>fxmanifest.lua</Tag> (or legacy <Tag>__resource.lua</Tag>) at the root. For a
          vehicle addon the minimal structure looks like this:
        </p>
        <CodeBlock>{`my_vehicle/
├── fxmanifest.lua
└── stream/
    ├── my_vehicle.yft
    ├── my_vehicle_hi.yft       # high-LOD, optional but recommended
    ├── my_vehicle.ytd
    ├── vehicles.meta
    ├── handling.meta
    ├── carvariations.meta
    └── carcols.meta`}</CodeBlock>
        <p className="text-sm leading-7 text-muted-foreground">
          A minimal <Tag>fxmanifest.lua</Tag> for a vehicle addon:
        </p>
        <CodeBlock>{`fx_version 'cerulean'
game 'gta5'

data_file 'VEHICLE_METADATA_FILE'  'stream/vehicles.meta'
data_file 'HANDLING_FILE'          'stream/handling.meta'
data_file 'VEHICLE_VARIATION_FILE' 'stream/carvariations.meta'
data_file 'CARCOLS_FILE'           'stream/carcols.meta'

files {
  'stream/*.yft',
  'stream/*.ytd',
}`}</CodeBlock>
        <Callout type="warn">
          Forgetting to declare{" "}
          <code className="font-mono text-xs">data_file</code> entries means the meta files are
          never loaded — the vehicle will spawn but may behave incorrectly or use default
          handling.
        </Callout>
      </>
    ),
  },
  {
    id: "eup-clothing-system",
    label: "EUP Clothing System",
    icon: <Shirt className="h-5 w-5" />,
    shortDescription: "Component slots, addon peds, and packing clothing resources.",
    seoTitle: "EUP Clothing System — FiveM Component Slots & Packing Guide",
    seoDescription:
      "How the EUP (Emergency Uniform Pack) clothing system works in FiveM — component ID slots, YDD/YTD pairing, and packing clothing into a streaming resource.",
    keywords: [
      "EUP clothing",
      "FiveM component slots",
      "EUP component id",
      "ydd clothing",
      "EUP FiveM setup",
      "Emergency Uniform Pack",
    ],
    toolHref: "/eup-viewer",
    toolLabel: "EUP Viewer",
    faqs: [
      {
        q: "What are EUP component slots?",
        a: "EUP maps each clothing piece to a GTA5 ped component ID — for example 3 = upper body, 4 = legs, 6 = shoes, 8 = undershirt, 9 = vest/armor, 11 = jacket. The slot decides where the drawable appears on the ped.",
      },
      {
        q: "Which component ID is a shirt or a jacket?",
        a: "Torso and arms are component 3, an undershirt is 8, and a jacket or coat overlay is 11. EUP Viewer reads the component ID straight out of each YDD so you don't have to guess.",
      },
      {
        q: "How do I pack EUP clothing for FiveM?",
        a: "Put the .ydd and its matching .ytd in the stream/ folder and reference them from fxmanifest.lua with a files{} block ('stream/*.ydd', 'stream/*.ytd') — the same structure as a vehicle addon.",
      },
      {
        q: "Why does my EUP clothing show a missing-texture warning?",
        a: "That's expected when a YDD references an external YTD you haven't loaded — inspect the YDD alone in EUP Viewer, and make sure the matching YTD ships alongside it in the resource.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Emergency Uniform Pack (EUP) adds civilian and emergency service clothing drawables to
          GTA5 using the base game's component system. Each clothing piece is a YDD file that
          maps to a component slot on the player ped.
        </p>
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/40">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-foreground">Component ID</th>
                <th className="px-4 py-3 text-left font-medium text-foreground">Slot</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {[
                [0, "Face"],
                [1, "Mask"],
                [3, "Upper body (torso + arms)"],
                [4, "Lower body (legs)"],
                [5, "Bag / parachute"],
                [6, "Shoes / feet"],
                [7, "Scarf / collar accessory"],
                [8, "Shirt / undershirt"],
                [9, "Body armor / vest"],
                [10, "Decals (badges, patches)"],
                [11, "Jacket / coat overlay"],
              ].map(([id, slot]) => (
                <tr key={id}>
                  <td className="px-4 py-2.5">
                    <Tag>{id}</Tag>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{slot}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Callout type="info">
          EUP Viewer shows the component ID embedded in each YDD file automatically — you don't
          need to look it up manually.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          EUP resources are structured similarly to vehicle addons. The YDD and its matching YTD
          must both be in the <code className="rounded bg-muted px-1 text-xs font-mono">stream/</code>{" "}
          folder, and the <Tag>fxmanifest.lua</Tag> must reference them via{" "}
          <code className="rounded bg-muted px-1 text-xs font-mono">files</code>.
        </p>
        <CodeBlock>{`files {
  'stream/*.ydd',
  'stream/*.ytd',
}`}</CodeBlock>
      </>
    ),
  },
  {
    id: "livery-texture-basics",
    label: "Livery & Texture Basics",
    icon: <Paintbrush2 className="h-5 w-5" />,
    shortDescription: "Multi-layer liveries, DDS formats, and the YTD pipeline.",
    seoTitle: "FiveM Livery & Texture Basics — DDS Formats & the YTD Pipeline",
    seoDescription:
      "How GTA5 vehicle liveries work — multi-layer YTD textures, DDS formats (BC1/BC3/BC7), power-of-two sizing, and the UV-template-to-in-game workflow for FiveM.",
    keywords: [
      "FiveM livery tutorial",
      "DDS format GTA5",
      "YTD texture",
      "vehicle livery template",
      "BC3 DXT5 livery",
      "power of two texture",
    ],
    toolHref: "/uv-studio",
    toolLabel: "UV Studio",
    faqs: [
      {
        q: "What DDS format should a FiveM livery use?",
        a: "Use BC3/DXT5 for anything with transparency (decals, livery overlays), BC1/DXT1 for fully opaque textures (smallest size), or BC7 when you need higher-quality gradients at a larger file size.",
      },
      {
        q: "Do livery textures have to be a power of two?",
        a: "Yes — 512, 1024, 2048, or 4096 px squares. Non-power-of-two textures cause rendering glitches in-game, so always size your canvas accordingly.",
      },
      {
        q: "How do I get a template to paint my livery on?",
        a: "Export a UV template from UV Studio to get a 1:1 map of the model's surface, paint the livery over it on separate layers in Photoshop, then export each layer to DDS and pack it into the YTD.",
      },
      {
        q: "How do I preview a livery before deploying it?",
        a: "Load your textures in Paint Booth to see the livery on the 3D model in real time — no in-game reload — before packing the final YTD.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          GTA5 vehicles support multi-layer liveries through the YTD texture dictionary system.
          Each texture slot in the YTD corresponds to a layer that can be independently colored
          or replaced. The base workflow is:
        </p>
        <StepList
          steps={[
            "Export the UV template from UV Studio to get a 1:1 map of the model's surface.",
            "Open the UV template in Photoshop or similar. Paint your livery on separate layers.",
            "Export each layer as a DDS (BC3/DXT5 for alpha, BC1/DXT1 for opaque).",
            "Use OpenIV to pack the DDS files into the vehicle's YTD.",
            "Preview the result in Paint Booth before deploying.",
          ]}
        />
        <Callout type="tip">
          DDS textures should always be powers of 2 (512×512, 1024×1024, 2048×2048, 4096×4096).
          Non-power-of-2 textures will cause rendering glitches in-game.
        </Callout>
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/40">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-foreground">DDS format</th>
                <th className="px-4 py-3 text-left font-medium text-foreground">Use case</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40 text-muted-foreground">
              <tr>
                <td className="px-4 py-2.5 font-mono text-xs">BC1 / DXT1</td>
                <td className="px-4 py-2.5">Opaque textures, no transparency needed. Smallest file size.</td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 font-mono text-xs">BC3 / DXT5</td>
                <td className="px-4 py-2.5">Textures with full alpha channel (decals, glass, livery overlays).</td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 font-mono text-xs">BC7</td>
                <td className="px-4 py-2.5">Higher quality alternative to DXT5. Larger files, better gradients.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    ),
  },
  {
    id: "carcols-and-carvariations",
    label: "Carcols & Carvariations",
    icon: <Code2 className="h-5 w-5" />,
    shortDescription: "How emergency lighting kits and livery slots are wired together.",
    seoTitle: "Carcols & Carvariations — FiveM Sirens, Liveries & Mod Kits",
    seoDescription:
      "How carcols.meta and carvariations.meta work together in FiveM — siren light kits, livery slots, and matching kit IDs. Wire them automatically with Easy Meta.",
    keywords: [
      "carcols.meta",
      "carvariations.meta",
      "FiveM siren kit",
      "livery slot FiveM",
      "kit id FiveM",
      "carcols carvariations",
    ],
    toolHref: "/easy-meta",
    toolLabel: "Easy Meta",
    faqs: [
      {
        q: "What's the difference between carcols.meta and carvariations.meta?",
        a: "carcols.meta defines siren/light kits and mod kits; carvariations.meta assigns liveries and links a vehicle to a kit by ID. The kit ID must match between the two files.",
      },
      {
        q: "Why is my livery slot blank in-game?",
        a: "Livery names in carvariations.meta are case-sensitive and must exactly match the texture names inside the vehicle's YTD. A single mismatch makes that slot render blank.",
      },
      {
        q: "How do I link a siren kit to my vehicle?",
        a: "The kit ID in carcols.meta must equal the one referenced in vehicles.meta and carvariations.meta. Easy Meta wires these IDs together automatically so they can't drift out of sync.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          These two files work together to define how your vehicle looks and lights up in-game.
        </p>
        <div className="space-y-4">
          <div className="rounded-xl border border-border/60 bg-card p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Code2 className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">carcols.meta — Kit structure</span>
            </div>
            <CodeBlock>{`<Item type="CVehicleModelInfoVarGlobal__Lights">
  <id value="1234" />
  <Kit>
    <id value="1234" />
    <name>my_vehicle_siren</name>
    <lightSettings>
      <!-- Each entry: position, color sequence, flash pattern -->
    </lightSettings>
  </Kit>
</Item>`}</CodeBlock>
            <p className="text-xs text-muted-foreground">
              The kit ID must match the one referenced in <Tag>vehicles.meta</Tag> — or use Easy
              Meta to wire them automatically.
            </p>
          </div>

          <div className="rounded-xl border border-border/60 bg-card p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">carvariations.meta — Livery entry</span>
            </div>
            <CodeBlock>{`<Item>
  <modelName>my_vehicle</modelName>
  <variationData>
    <Item>
      <liveries0>
        <Item>my_vehicle_livery1</Item>
        <Item>my_vehicle_livery2</Item>
      </liveries0>
    </Item>
  </variationData>
</Item>`}</CodeBlock>
            <p className="text-xs text-muted-foreground">
              Each string in{" "}
              <code className="rounded bg-muted px-1 text-xs font-mono">liveries0</code> is the
              name of a texture inside the vehicle's YTD. They must match exactly
              (case-sensitive).
            </p>
          </div>

          <Callout type="warn">
            Livery texture names in <Tag>carvariations.meta</Tag> are case-sensitive and must
            exactly match the texture names inside the YTD. A mismatch causes the livery slot to
            appear blank in-game.
          </Callout>
        </div>
      </>
    ),
  },
  {
    id: "32-siren-script",
    label: "32 Siren Script",
    icon: <Siren className="h-5 w-5" />,
    shortDescription: "Extending GTA5's siren slot limit from 20 up to 32.",
    seoTitle: "32 Siren Script — Raise FiveM's Siren Limit to 32 (Setup Guide)",
    seoDescription:
      "How to install the SirenSetting Limit Adjuster ASI to extend GTA5's 20-siren cap to 32 in FiveM, use siren21–siren32 in carcols.meta, and avoid the 128-bone limit.",
    keywords: [
      "32 siren script",
      "SirenSetting Limit Adjuster",
      "FiveM siren limit",
      "siren21 carcols",
      "extended siren slots",
      "FiveM lightbar limit",
    ],
    toolHref: "/blinklab",
    toolLabel: "Blink Lab",
    faqs: [
      {
        q: "How many sirens does GTA5 support by default?",
        a: "Twenty siren entries per vehicle. The SirenSetting Limit Adjuster ASI raises the cap to 32 and allows siren IDs above 255 for more complex lighting.",
      },
      {
        q: "Where does the SirenSetting Limit Adjuster .asi go?",
        a: "Drop it in your FiveM plugins folder (…\\FiveM\\FiveM.app\\plugins). It loads automatically on launch — just make sure your server build is no newer than 2699.",
      },
      {
        q: "Does the 32-siren plugin remove the bone limit?",
        a: "No. A vehicle model still has a hard 128-bone limit — extra siren slots don't bypass it, so budget your bones when planning a complex lightbar.",
      },
      {
        q: "How do I use the extra siren slots?",
        a: "Once installed, use siren21 through siren32 in carcols.meta exactly like the standard slots. Avoid the reserved IDs 0, 255, and 65535, and build the patterns visually in Blink Lab.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          By default GTA5 supports up to 20 siren entries per vehicle. The{" "}
          <strong>SirenSetting Limit Adjuster</strong> is an ASI plugin that raises this cap to
          32 sirens and extends siren IDs beyond 255, allowing far more complex emergency
          lighting setups on a single vehicle.
        </p>

        <p className="text-sm font-medium text-foreground">Installation — FiveM client</p>
        <StepList
          steps={[
            <>Download the <strong>SirenSetting_Limit_Adjuster.asi</strong> file.</>,
            <>Navigate to your FiveM plugins folder: <code className="rounded bg-muted px-1 text-xs font-mono">C:\Users\[Your_user]\AppData\Local\FiveM\FiveM.app\plugins</code></>,
            <>Place the <Tag>.asi</Tag> file in that folder.</>,
            <>Ensure your server build is <strong>no newer than 2699</strong>.</>,
            <>Restart FiveM — the plugin loads automatically on launch.</>,
          ]}
        />

        <p className="text-sm font-medium text-foreground">Developer usage</p>
        <p className="text-sm leading-7 text-muted-foreground">
          Once the plugin is installed, you can use{" "}
          <code className="rounded bg-muted px-1 text-xs font-mono">siren21</code> through{" "}
          <code className="rounded bg-muted px-1 text-xs font-mono">siren32</code> in your{" "}
          <Tag>carcols.meta</Tag> entries just like the standard siren slots. IDs above 255 are
          also supported — avoid the reserved values <strong>0</strong>, <strong>255</strong>,
          and <strong>65535</strong>.
        </p>
        <CodeBlock>{`<!-- carcols.meta example using extended siren slots -->
<sirenSettings>
  <siren21>
    <rotation rotationDelta="0" ... />
    <flashiness timeMultiplier="1.0" ... />
    <corona ... />
  </siren21>
  <siren32>
    <!-- additional light definition -->
  </siren32>
</sirenSettings>`}</CodeBlock>
        <Callout type="warn">
          There is still a hard limit of <strong>128 total bones</strong> on a single vehicle
          model. Adding extra siren slots does not bypass this — plan your model's bone budget
          accordingly.
        </Callout>
        <p className="text-sm font-medium text-foreground">Logging</p>
        <p className="text-sm leading-7 text-muted-foreground">
          The plugin generates a{" "}
          <code className="rounded bg-muted px-1 text-xs font-mono">SirenSettings.log</code>{" "}
          file each session. It documents every loaded siren setting, which vehicles reference
          it, and any ID conflicts detected at load time. Check this file first when lights
          behave unexpectedly.
        </p>
        <Callout type="info">
          The ASI works independently of RAGEPluginHook (RPH) but is compatible with the latest
          RPH version. If you previously installed compatibility DLLs for an older version, they
          can be safely removed. Emergency lighting RPH plugins (e.g. ELS) may require an update
          to work with extended siren entries.
        </Callout>
      </>
    ),
  },
  {
    id: "fixing-modkit-ids",
    label: "Fixing Modkit ID Conflicts",
    icon: <Wrench className="h-5 w-5" />,
    shortDescription: "Why two cars share liveries in the mod shop, and how to fix it.",
    seoTitle: "Fixing FiveM Modkit ID Conflicts — Shared Liveries & Parts",
    seoDescription:
      "Why two FiveM vehicles share liveries or mod-shop parts, and how to fix modkit ID conflicts in carcols.meta and carvariations.meta. Easy Meta assigns unique kit IDs for you.",
    keywords: [
      "modkit id conflict",
      "FiveM shared liveries",
      "kitNumber carvariations",
      "FiveM mod shop bug",
      "unique kit id",
      "carcols kit id",
    ],
    toolHref: "/easy-meta",
    toolLabel: "Easy Meta",
    faqs: [
      {
        q: "Why do two of my FiveM cars share liveries or parts?",
        a: "They've been given the same modkit ID. When two vehicles declare an identical kit ID, one car's bumpers, spoilers, or livery slots bleed onto the other in Los Santos Customs.",
      },
      {
        q: "How do I fix a modkit ID conflict?",
        a: "Change the <id> inside the <Kit> block in carcols.meta and the matching kitNumber in carvariations.meta to a new unique value (above 500 is safe), update both files identically, then restart the resource and test in LSC.",
      },
      {
        q: "What kit ID values are safe to use?",
        a: "Numbers above 500 are rarely used by vanilla vehicles. Avoid 0, 255, and 65535, which are reserved by the engine, and keep a server-wide list so every custom vehicle uses a different ID.",
      },
      {
        q: "How do I avoid modkit conflicts entirely?",
        a: "Assign a unique kit ID to every custom vehicle on the server — Easy Meta does this automatically when you generate meta files, so IDs stay unique across your fleet.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          A <strong>modkit</strong> defines the visual customisation options for a vehicle —
          bumpers, spoilers, wheels, livery slots, and so on. Every vehicle that uses mods must
          declare a kit in <Tag>carcols.meta</Tag> and reference the same kit number in{" "}
          <Tag>carvariations.meta</Tag>. When two vehicles on the same server share an identical
          modkit ID, one vehicle's parts will visually bleed onto the other in the mod shop.
        </p>

        <p className="text-sm font-medium text-foreground">Symptoms of a modkit ID conflict</p>
        <BulletList
          items={[
            "Another vehicle's spoilers or bumpers appear in your vehicle's mod shop.",
            "Selecting a livery slot changes a completely different vehicle's appearance.",
            "Extras toggle on the wrong car.",
            "Tuning parts disappear or show 0 options in Los Santos Customs.",
          ]}
        />

        <p className="text-sm font-medium text-foreground">How to fix it</p>
        <StepList
          steps={[
            <>Open your vehicle's <Tag>carcols.meta</Tag> and find the <code className="rounded bg-muted px-1 text-xs font-mono">&lt;id value="..." /&gt;</code> inside the <code className="rounded bg-muted px-1 text-xs font-mono">&lt;Kit&gt;</code> block.</>,
            <>Open your <Tag>carvariations.meta</Tag> and find the matching <code className="rounded bg-muted px-1 text-xs font-mono">&lt;kitNumber value="..." /&gt;</code>.</>,
            <>Pick a new unique number. Values above <strong>500</strong> are rarely used by vanilla vehicles and are a safe range. Avoid 0, 255, and 65535 (reserved by the engine).</>,
            <>Update <strong>both</strong> files to the new ID — they must match exactly.</>,
            <>If you're running multiple custom vehicles on the same server, make sure every vehicle uses a <em>different</em> kit ID. Keep a server-wide ID list to prevent future conflicts.</>,
            <>Restart the resource and test in Los Santos Customs.</>,
          ]}
        />

        <p className="text-sm font-medium text-foreground">Where to change the ID</p>
        <CodeBlock>{`<!-- carcols.meta -->
<Item type="CVehicleModelInfoVarGlobal">
  <id value="530" />          <!-- ← change this -->
  <Kit>
    <id value="530" />        <!-- ← and this (must match) -->
    <name>my_vehicle_mods</name>
    ...
  </Kit>
</Item>`}</CodeBlock>
        <CodeBlock>{`<!-- carvariations.meta -->
<Item>
  <modelName>my_vehicle</modelName>
  <variationData>
    <Item>
      <kitNumber value="530" />   <!-- ← must match carcols id -->
      ...
    </Item>
  </variationData>
</Item>`}</CodeBlock>
        <Callout type="tip">
          A common convention is to derive the kit ID from the vehicle's spawn name hash, or to
          simply assign incrementing IDs starting at 600 for all custom vehicles on the server
          and document them in a shared spreadsheet.
        </Callout>
        <Callout type="warn">
          Easy Meta handles kit ID assignment automatically when you generate meta files — just
          make sure you enter a unique kit ID in the form before exporting.
        </Callout>
      </>
    ),
  },
];
